import { AuthData } from '../model/auth-data.model';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { AppState, selectAuthDataCommonState } from 'app/app-state.index';
import { clearAuthData, setAuthData, setUserInfo } from '../state/actions/user.actions';
import { catchError, map, share, tap } from 'rxjs/operators';
import { Language, LoginStatus, Role } from '../model/enums';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { LocalizationService } from '../localization/localization.service';




@Injectable()
export class AppAuthService {

    private _authData: AuthData;
    private changePasswordToken: string;

    constructor(private router: Router, private httpClient: HttpClient,
        private store: Store<AppState>, private ngZone: NgZone, private localizationService: LocalizationService) {
        this.store.select(selectAuthDataCommonState).subscribe((authData: AuthData) => {
            this._authData = authData;
        });

        setTimeout(() => {
            this.initExpireSessionTimer();
        }, 1);

    }

    //#region Private Members   
    sessionExpireIntervalMinutes = 5;
    refreshSessionTimer: any;
    //#endregion

    //#region Public getters

    get authToken(): string {
        return this._authData?.token;
    }
    get authEmail(): string {
        return this._authData?.email;
    }
    get authName(): string {
        return this._authData?.userName;
    }
    get SessionExpiry(): boolean {
        return !this.authToken;
    }
    get isLoggedOn(): boolean {
        return this.isSessionActive;
    }
    get isSessionActive(): boolean {
        return !this.SessionExpiry;
    }

    //#endregion

    refreshSession() {
        const intervalCheckInMs = 60 * 1000 *60 ; // one hour
        const sessionEndTime = Date.now() + this.sessionExpireIntervalMinutes * 60 * 1000;
        if (this.refreshSessionTimer) {
            clearInterval(this.refreshSessionTimer)
        }
        this.refreshSessionTimer = setInterval(() => {
            if (Date.now() >= sessionEndTime) {
                clearInterval(this.refreshSessionTimer);
                this.clientLogout();
            }
        }, intervalCheckInMs);
        
    }
    login(userName: string, password: string) {
        this.clearLogin();
        return this.httpClient.post<{
            status: LoginStatus,
            token: string,
            name: string,
            email: string
        }>('/auth/LoginWebRolleta', { userName, password }).pipe(map((response: any) => {
            if (response.ErrorCode) {
                return { status: LoginStatus.InvalidLogin }
            }
            this._authData = {
                token: response.SessionId, email: response.AuthData.Email, businessName: response.AuthData.BusinessName, ownerFullName: response.AuthData.OwnerFullName,isAtn:response.AuthData.IsAtn
            };
            this.store.dispatch(setAuthData(this._authData));
            if (response.Language) {
                this.localizationService.changeLanguage(response.Language);
            }
            return { status: LoginStatus.Succeeded };
        }));
    }
    handleGoogleCredentialResponse(response: any) {
        const token = JSON.stringify(response.credential);        
        return this.httpClient.post<{ status: LoginStatus, token: string, name: string, email: string }>(
            '/auth/LoginWebRolletaByGoogleSign',
            token 
        ).pipe(
            map((response: any) => {
                if (response.ErrorCode) {
                    return { status: LoginStatus.InvalidLogin };
                }
                this._authData = {
                    token: response.SessionId,
                    email: response.AuthData.Email,
                    businessName: response.AuthData.BusinessName,
                    ownerFullName: response.AuthData.OwnerFullName,
                    isAtn:response.AuthData.IsAtn
                };
                this.store.dispatch(setAuthData(this._authData));
                if (response.Language) {
                    this.localizationService.changeLanguage(response.Language);
                }
                return { status: LoginStatus.Succeeded };
            })
        );
    }
    registerByGoogle(token: string,registerModel:any):Observable<any> {
        registerModel.token=token
        return this.httpClient.post('/auth/registerByGoogle', registerModel ).pipe(share());
    }
    getOrLoadUserInfo() {
        if (this.authName) {
            return of(true);
        } else {
            return this.loadUserInfo();
        }
    }

    loadUserInfo() {
        return this.httpClient.get<{
            BusinessName: string,
            OwnerFullName: string,
            Email: string,
            Language: Language,
            IsAtn:boolean
        }>('/auth/getUserInfo')
            .pipe(catchError((err) => {
                this.clearLogin();
                return EMPTY;
            }))
            .pipe(tap(async (response) => {
                this.store.dispatch(setUserInfo({ businessName: response.BusinessName, ownerFullName: response.OwnerFullName,isAtn:response.IsAtn }));
                if (response.Language) {
                    this.localizationService.changeLanguage(response.Language);
                }
            })).pipe(map(() => true));
    }

    signup(request: any): Observable<any> {
        return this.httpClient.post('/auth/signup', request);
    }

    sendRecoverPasswordVerification(request): Observable<any> {
        return this.httpClient.post('/auth/recoverPasswordVerification', request);
    }

    verifyRecoverPasswordCode(request): Observable<any> {
        return this.httpClient.post('/auth/checkVerificationCode', request).pipe(tap(response => {
            this.changePasswordToken = response.token;
        }));
    }

    changePassword(request) {
        return this.httpClient.post('/auth/changePassword', request);
    }
    updatePassword(request){
        return this.httpClient.post('/auth/updatePassword', request);
    }
    logout() {
        this.httpClient.post("/auth/logout", null).subscribe(() => {
        });
        this.clientLogout();
    }

    clientLogout() {
        this.clearLogin();
        this.ngZone.run(() => {
            this.router.navigate(['/']);
        })
        this.router.navigate(['/']);
    }
    register(request): Observable<any> {
        return this.httpClient.post('/auth/register', request).pipe(share());
    }
    //#region Private Methods

    private initExpireSessionTimer() {
        this.httpClient.get('/auth/getSessionTimeInMinutes').subscribe((res: any) => {
            this.sessionExpireIntervalMinutes = res.MinuteToAdd;
            if (this.isLoggedOn) {
                this.refreshSession()
            }
        })
    }
    private clearLogin() {
        this.store.dispatch(clearAuthData());
    }
    //#endregion
}


